import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AbstractControl } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';

import { AppGlobals } from '@app/app.global';
import { environment } from '@env/environment';
import { ILanguage } from '@core/interfaces';
import { Language } from '@core/entities';
import { AuthService } from '@core/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  sideBarToggle = new EventEmitter<Boolean>();
  languagesSubject: BehaviorSubject<Language[]>;

  constructor(
    private toastr: ToastrService,
    private http: HttpClient,
    private appGlobals: AppGlobals,
    private translateService: TranslateService,
    private authService: AuthService
  ) {
    this.languagesSubject = new BehaviorSubject<Language[]>([]);
  }

  getLanguages() {
    this.translateService.setDefaultLang('en-US');
    this.translateService.use('en-US');
    return this.http.get<any>(`${environment.apiUrl}languages/`).pipe(
      map((languages: ILanguage[]) =>
        languages.map((language: ILanguage) => new Language(language))
      ),
      catchError(error => {
        console.error(error);
        const languages = [new Language({ code: 'en', name: 'English' })];
        this.languagesSubject.next(languages);
        return languages;
      }),
      tap((languages: Language[]) => this.languagesSubject.next(languages))
    );
  }

  changeLanguage(language: string, locale?: string) {
    const langaugeSplit = language.split('-');
    if (!locale) {
      if (langaugeSplit.length > 1) {
        locale = langaugeSplit[1];
        language = langaugeSplit[0];
      } else {
        language = language || langaugeSplit[0] || this.appGlobals.defaultLanguage;
        locale = this.appGlobals.defaultLocale;
      }
    } else {
      if (langaugeSplit.length > 1) {
        locale = langaugeSplit[1] || locale;
        language = langaugeSplit[0];
      } else {
        language = language || langaugeSplit[0] || this.appGlobals.defaultLanguage;
        locale = this.appGlobals.defaultLocale;
      }
    }

    const langWithLocale = `${language}-${locale}`;

    this.translateService.use(langWithLocale);
    localStorage.setItem('probankerCurrentLanguage', langWithLocale);
  }

  get currentLanguage() {
    return this.translateService.currentLang;
  }

  get isListView(): boolean {
    if (this.authService.user.isPlayer && localStorage.getItem('probankerIsListView')) {
      return localStorage.getItem('probankerIsListView') === 'true';
    } else {
      return !this.authService.user.isPlayer;
    }
  }

  set isListView(value: boolean) {
    localStorage.setItem('probankerIsListView', value ? 'true' : 'false');
  }

  successAlert(message?: string) {
    this.toastr.success(message, '', {
      closeButton: true,
      timeOut: 15000,
      toastClass: `pmd-toastr toast-success ngx-toastr`,
    });
  }

  errorAlert(message?: string) {
    this.toastr.error(message, '', {
      closeButton: true,
      timeOut: 15000,
      toastClass: `pmd-toastr toast-Error ngx-toastr`,
    });
  }

  toggleSideBar() {
    this.sideBarToggle.emit();
  }

  formatBytes(bytes: number) {
    if (bytes === 0) return '0 Bytes';

    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));

    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  }

  isFieldDirty(field: AbstractControl, isSubmitAttempted: boolean) {
    return field.dirty || field.touched || isSubmitAttempted;
  }

  openFile(path: string) {
    const link = document.createElement('a');

    link.setAttribute('type', 'hidden');
    link.href = path;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  downloadFile(path: string, fileName?: string) {
    const link = document.createElement('a');

    link.setAttribute('type', 'hidden');
    link.href = path;
    link.download = fileName ?? path.split('/').pop();
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  downloadBlob(data: Blob, fileName?: string) {
    const link = document.createElement('a');

    link.setAttribute('type', 'hidden');
    link.href = window.URL.createObjectURL(data);
    link.download = fileName ?? 'untitled';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => document.body.removeChild(link), 0);
  }
}
